export interface WidgetConfig {
  title: string;
  greeting: string;
  language: string;
  primaryColor: string;
  position: string;
  sessionTimeout: number;
  displayMode: string;
  systemPrompt: string;
  maxToolIterations: number;
  allowedTools: number[];
  dangerousTools: number[];
  dataAnalystEnabled: boolean;
  dataAnalystCheckerEnabled: boolean;
  authProvider: string;
  otpEnabled: boolean;
  otpGreeting: string;
  providerGreetings: Record<string, string>;
  otpProvider: string;
  smtpHost: string;
  smtpPort: number;
  smtpUser: string;
  smtpPass: string;
  smtpFrom: string;
  uploadEnabled: boolean;
  uploadAllowedTypes: string[];
  uploadMaxSizeMb: number;
  uploadS3ConnectionId: string;
  voiceEnabled: boolean;
  drivingModeEnabled: boolean;
  voiceOverride: boolean;
  voiceTtsProvider: string;
  voiceTtsModel: string;
  voiceTtsVoice: string;
  voiceApiKeyOpenai: string;
  voiceGoogleApiKey: string;
  voiceGoogleVoice: string;
  voiceGoogleEnVoice: string;
  voiceElevenLabsApiKey: string;
  voiceElevenLabsVoiceId: string;
}

export interface WidgetTokenUsage {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  monthlyLimit: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
}

export interface WidgetConnectorOption {
  id: string;
  connectionName: string;
  environment?: string;
  connectionTypeId?: number;
}

export const DEFAULT_WIDGET_CONFIG: WidgetConfig = {
  title: 'Support Chat',
  greeting: 'Hello! How can we help you today?',
  language: 'en',
  primaryColor: '#4f46e5',
  position: 'bottom-right',
  sessionTimeout: 10,
  displayMode: 'floating',
  systemPrompt: '',
  maxToolIterations: 10,
  allowedTools: [],
  dangerousTools: [],
  dataAnalystEnabled: false,
  dataAnalystCheckerEnabled: true,
  authProvider: 'none',
  otpEnabled: false,
  otpGreeting: '',
  providerGreetings: {},
  otpProvider: 'email',
  smtpHost: '',
  smtpPort: 587,
  smtpUser: '',
  smtpPass: '',
  smtpFrom: 'noreply@example.com',
  uploadEnabled: true,
  uploadAllowedTypes: ['application/pdf', 'image/png', 'image/jpeg', 'image/gif'],
  uploadMaxSizeMb: 10,
  uploadS3ConnectionId: '',
  voiceEnabled: false,
  drivingModeEnabled: false,
  voiceOverride: false,
  voiceTtsProvider: 'openai',
  voiceTtsModel: 'tts-1',
  voiceTtsVoice: 'nova',
  voiceApiKeyOpenai: '',
  voiceGoogleApiKey: '',
  voiceGoogleVoice: 'he-IL-Wavenet-D',
  voiceGoogleEnVoice: 'en-US-Wavenet-D',
  voiceElevenLabsApiKey: '',
  voiceElevenLabsVoiceId: '',
};

export const WIDGET_STYLE_COLORS = ['#4f46e5', '#222035', '#059669', '#dc2626', '#0284c7', '#7c3aed', '#ea580c', '#000000'];

export const WIDGET_LANGUAGE_DEFAULTS: Record<string, { title: string; greeting: string }> = {
  en: { title: 'Support Chat', greeting: 'Hello! How can we help you today?' },
  he: { title: "צ'אט שירות לקוחות", greeting: 'שלום! איך אפשר לעזור?' },
};

export function normalizeWidgetConfig(config?: Partial<WidgetConfig> | Record<string, unknown>): WidgetConfig {
  const normalized = { ...DEFAULT_WIDGET_CONFIG };

  if (!config) {
    return normalized;
  }

  for (const key of Object.keys(DEFAULT_WIDGET_CONFIG) as Array<keyof WidgetConfig>) {
    const value = config[key];
    if (value !== undefined && value !== null) {
      (normalized as Record<keyof WidgetConfig, unknown>)[key] = value;
    }
  }

  if (!normalized.voiceEnabled) {
    normalized.drivingModeEnabled = false;
  }

  return normalized;
}

